import {
    IDownloadBroadcast,
    IDownloadBroadcastRaw,
    IDownloadData,
    IDownloadDeviceRaw,
    IDownloadDevice,
    IDownloadFile,
    IDownloadInfo,
    IDownloadRaw,
    IDownloadUnkown,
    TDownloadType,
} from 'core/models/Downloads'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { generateIdByUUID, isNotEmptyArray } from 'core/utils/coreUtil'
import helpers from 'core/helpers'
import { downloadStartNotification } from 'blocks.app/app/__notifications/app__notifications'
import { listeners } from 'core/helpers'
import { api } from 'core/api/ConnectionManager'
import { IFile, IUploadFile, IUploadSourceFile } from 'core/models/Files'
import { emitError } from 'features/appNotifications/AppNotifications.state'
import { createRequestHeaders } from 'core/helpers/user'
import { isFunction } from 'lodash'
import { IUserState } from 'blocks.app/user/user.state'
import { getURLSearchParamsByLocation } from '../../../features/routes/utils'
import { history } from '../../../features/routes'

export interface UploadFilesPayload {
    filesList: IUploadSourceFile[]
    folderId?: string
    broadcast?: boolean
    importAdvertisement?: boolean
    withConversion?: string
    onUploaded?: (response: unknown) => void
}

export type DelayDownloadTask = {
    type: string
    targetId: number
    exportId: string
}

type DelayDownloadsProgress = {
    name: string
    percent: number
    exportId: string
}

export interface IDownloadsState {
    downloads: IDownloadData[]
    cachedDownloads: IDownloadData[]
    cachedUploads: IDownloadData[]
    uploads: IDownloadData[]
    isUploading: boolean
    isOpenModal: boolean
    isShowNotification: boolean
    queue: string[]
    delayDownloadTasks: DelayDownloadTask[]
    delayDownloadsProgressList: DelayDownloadsProgress[]
}

const MAX_QUEUE_LENGTH = 20
const uploadFileMap = new Map<string, IUploadFile>()

let cachedDownloads: IDownloadData[] = []
let cachedUploads: IDownloadData[] = []

export const initialState: IDownloadsState = {
    cachedDownloads: [],
    downloads: [],
    cachedUploads: [],
    uploads: [],
    isUploading: false,
    isOpenModal: false,
    isShowNotification: true,
    queue: [],
    delayDownloadTasks: [],
    delayDownloadsProgressList: [],
}

const setUploadFile = (file: IUploadFile) => {
    uploadFileMap.set(file.id, file)
}

const deleteUploadFile = (id: string) => {
    return uploadFileMap.delete(id)
}

const getUploadFile = (id: string) => {
    return uploadFileMap.get(id)
}

const cancelUploadFile = (id: string) => {
    const uploadFile = getUploadFile(id)

    if (!uploadFile || !isFunction(uploadFile.cancel)) return

    uploadFile.cancel()
}

const updateUploadFileCancel = (id: string, cancelFn: () => void) => {
    const uploadFile = getUploadFile(id)

    if (!uploadFile) return

    uploadFile.cancel = cancelFn

    uploadFileMap.set(id, uploadFile)
}

const createFileFormData = (
    folderId: string | undefined,
    file: IUploadSourceFile | IUploadSourceFile[],
    withConversion?: string
) => {
    let formData = new FormData()
    if (folderId) {
        formData.append('folderId', folderId)
    }

    if (Array.isArray(file)) {
        file.forEach((el) => {
            formData.append('file', el)
        })
    } else {
        formData.append('file', file)
    }
    withConversion && formData.append('withConversion', withConversion)
    return formData
}

const showNotification = (addedDownload: IDownloadRaw) => {
    helpers.debounce(
        () => {
            downloadStartNotification(addedDownload)
        },
        { limit: 1000, id: 'download_global_notification' }
    )
}

const isValidDownload = ({ progress, children }: IDownloadData) => {
    const hasValidProgress = typeof progress === 'number' && !isNaN(progress)
    const hasValidChildren = children === undefined || children.length > 0

    return hasValidProgress && hasValidChildren
}

const getDownloadDevices = (download: Pick<IDownloadRaw, 'digitalSignage'>): IDownloadDeviceRaw[] => {
    if (!Array.isArray(download.digitalSignage)) return []

    return download.digitalSignage.filter((device): device is IDownloadDeviceRaw => device !== null)
}

const getDeviceProgress = (progress: unknown): number => {
    const parsedProgress = Number(progress)

    return Number.isFinite(parsedProgress) ? parsedProgress : 0
}

const isEqual = <T extends Partial<IDownloadInfo>, N extends Partial<IDownloadInfo>>(data: T, comparedData: N) => {
    return data.id === comparedData.id && data.type === comparedData.type
}

const isSourceDownloadType = (type: TDownloadType) => type === 'sourceFromUrl'

const updateProgress = (download: IDownloadBroadcastRaw) => {
    let broadcastProgress = 0
    const digitalSignages = getDownloadDevices(download)

    if (!digitalSignages.length) return

    digitalSignages.forEach((display) => {
        const progress = getDeviceProgress(display.progress)
        broadcastProgress += progress

        listeners.updateDisplayProgress({
            id: display.id,
            cover: 'ignore',
            downloadPercentage: progress === 100 ? [] : [{ percentage: progress, title: display.name }],
        })
    })

    const roundedBroadcastProgress = parseFloat((broadcastProgress / digitalSignages.length).toFixed(2))
    updateBroadcastProgress(download, roundedBroadcastProgress)
}

const updateBroadcastProgress = (download: Pick<IDownloadBroadcastRaw, 'id' | 'name'>, progress: number) => {
    listeners.updateBroadcastProgress({
        id: download.id,
        cover: 'ignore',
        downloadPercentage: progress === 100 ? [] : [{ percentage: progress, title: download.name }],
    })
}

const createDownload = (download: IDownloadRaw): IDownloadData => {
    if (isSourceDownloadType(download.type)) return download as IDownloadFile

    let progress = 0

    const digitalSignages = getDownloadDevices(download)
    const downloadChildren = digitalSignages.map((device) => {
        const type: TDownloadType = 'digitalSignage'
        const progressValue = getDeviceProgress(device.progress)
        const deviceProgress = parseFloat(progressValue.toFixed(2))
        progress += progressValue

        return {
            id: device.id,
            name: device.name,
            children: device.files,
            type,
            parent: {
                id: download.id,
                type: download.type,
            },
            cancel: true,
            progress: deviceProgress,
        }
    })

    const broadcastProgress = downloadChildren.length ? parseFloat((progress / downloadChildren.length).toFixed(2)) : 0

    return {
        id: download.id,
        name: download.name,
        type: download.type,
        progress: broadcastProgress,
        cancel: true,
        children: downloadChildren,
    }
}

const addDownload = (downloads: IDownloadData[], addedDownload: IDownloadRaw): IDownloadData[] => {
    switch (addedDownload.type) {
        case 'file':
        case 'sourceFromUrl':
            return [addedDownload as IDownloadFile, ...downloads]
        default:
            updateProgress(addedDownload as IDownloadBroadcastRaw)
            const createdDownload = createDownload(addedDownload) as IDownloadBroadcast

            if (!createdDownload.children.length) return downloads

            const isExistDownload = downloads.find((download: IDownloadData) => isEqual(download, createdDownload))

            if (!isExistDownload) {
                return [createdDownload, ...downloads]
            }

            return downloads.map((download: IDownloadData) => {
                if (!isEqual(download, createdDownload)) return download

                let downloadBroadcast = download as IDownloadBroadcast
                let isExistDevice = !!downloadBroadcast.children.find((device) =>
                    isEqual(device, createdDownload.children[0])
                )

                return isExistDevice
                    ? download
                    : { ...download, children: [...downloadBroadcast.children, ...createdDownload.children] }
            })
    }
}

const updateDownload = (downloads: IDownloadData[], updatedDownload: IDownloadRaw) => {
    return downloads.map((download) => {
        if (!isEqual(download, updatedDownload)) return download

        switch (updatedDownload.type) {
            case 'file':
            case 'sourceFromUrl':
                return {
                    ...download,
                    progress: updatedDownload.progress,
                } as IDownloadData
            default:
                updateProgress(updatedDownload as IDownloadBroadcastRaw)
                const createdDownload = createDownload(updatedDownload) as IDownloadBroadcast
                const downloadBroadcast = download as IDownloadBroadcast
                const constructedChild = createdDownload.children[0]

                if (!constructedChild) return download

                let progress = 0

                const children = downloadBroadcast.children.map((child) => {
                    if (isEqual(child, constructedChild)) {
                        progress += constructedChild.progress
                        return constructedChild
                    }

                    progress += child.progress
                    return child
                })

                const formattedProgress = parseFloat((progress / children.length).toFixed(2))

                return {
                    ...download,
                    progress: formattedProgress,
                    children,
                }
        }
    })
}

const removeDigitalSignage = (downloads: IDownloadData[], { parent, id }: IDownloadDevice) => {
    return downloads.reduce((filtered: IDownloadData[], download) => {
        if (!isEqual(download, parent)) return [...filtered, download]

        const downloadBroadcast = download as IDownloadBroadcast
        const children = downloadBroadcast.children.filter((device) => !(device.id === id))

        if (children.length > 0) return [...filtered, { ...download, children }]

        return filtered
    }, [])
}

const updateLoading = (payload: IDownloadRaw) => {
    const { progress, id } = payload

    if (!progress) return

    cachedUploads = cachedUploads.map((upload) => (upload.id === id ? { ...upload, progress } : upload))
    cachedDownloads = cachedDownloads.map((download) => (download.id === id ? { ...download, progress } : download))
}

export const cancelUpload = createAsyncThunk('downloads/cancelUpload', async (id: string, { dispatch }) => {
    cancelUploadFile(id)
    deleteUploadFile(id)
    dispatch(downloadsActions.removeLoading(id))
})

export const cancelUploadByUrl = (id: string) => {
    api.send('cancelDownloadByUrl', { id })
}

export const cancelFileDownload = createAsyncThunk(
    'donwloads/cancelFileDownload',
    async (download: IDownloadFile, { dispatch }) => {
        cancelUploadFile(download.id)
        deleteUploadFile(download.id)
        dispatch(downloadsActions.removeLoading(download.id))
    }
)

export const cancelDeviceDownload = createAsyncThunk(
    'donwloads/cancelDeviceDownload',
    async (download: IDownloadDevice, { dispatch }) => {
        const isComplete = Math.floor(download.progress) === 100

        if (isComplete) {
            dispatch(downloadsActions.removeDigitalSignage(download))
            return
        }

        try {
            await api.send('cancelClientDownload', {
                instanceId: download.parent.id,
                instanceType: download.parent.type,
                digitalSignageId: download.id,
            })

            dispatch(downloadsActions.removeDigitalSignage(download))
        } catch (err) {}
    }
)

export const cancelBroadcastDownload = createAsyncThunk(
    'donwloads/cancelBroadcastDownload',
    async (download: IDownloadBroadcast, { dispatch }) => {
        const isComplete = Math.floor(download.progress) === 100

        if (isComplete) {
            dispatch(downloadsActions.removeDownload(download))
            return
        }

        try {
            const uncompletedDeviceDownloads = download.children.reduce((filtered: string[], child) => {
                const isUncomplete = Math.floor(child.progress) !== 100
                return isUncomplete ? [...filtered, child.id] : filtered
            }, [])

            await api.send('cancelClientDownload', {
                instanceId: download.id,
                instanceType: download.type,
                digitalSignageId: uncompletedDeviceDownloads,
            })

            dispatch(downloadsActions.removeDownload(download))
        } catch (err) {}
    }
)

export const cancelDownload = createAsyncThunk(
    'downloads/cancelDownload',
    async (download: IDownloadUnkown, { dispatch }) => {
        switch (download.type) {
            case 'sourceFromUrl':
            case 'file':
                dispatch(cancelFileDownload(download as IDownloadFile))
                break
            case 'digitalSignage':
                dispatch(cancelDeviceDownload(download as IDownloadDevice))
                break
            default:
                dispatch(cancelBroadcastDownload(download as IDownloadBroadcast))
                break
        }
    }
)

export const cancelDownloadWithoutMessage = createAsyncThunk(
    'downloads/cancelDownloadWithoutMessage',
    async (download: IDownloadUnkown, { dispatch }) => {
        switch (download.type) {
            case 'digitalSignage':
                dispatch(downloadsActions.removeDigitalSignage(download as IDownloadDevice))
                break
            case 'sourceFromUrl':
            case 'file':
                dispatch(cancelFileDownload(download as IDownloadFile))
                break
            default:
                dispatch(downloadsActions.removeDownload(download as IDownloadBroadcast))
                break
        }
    }
)

export const emitFileUploadError = createAsyncThunk(
    'downloads/emitFileUploadError',
    async (err: number, { dispatch }) => {
        switch (err) {
            case 27:
                dispatch(emitError('27'))
                break
            case 64:
                dispatch(emitError('64'))
                break
            case 123:
                dispatch(emitError('123'))
                break
            default:
                dispatch(emitError('uploadError'))
        }
    }
)

const uploadFile = createAsyncThunk('downloads/uploadFile', async (id: string, { dispatch, getState }) => {
    const state = getState() as { user: IUserState }
    const { data: user, mainUser, tokens } = state.user

    if (!user || !mainUser || !tokens) return

    let isCanceled = false
    const upload = getUploadFile(id)

    if (!upload) {
        dispatch(downloadsActions.removeUpload(id))
        dispatch(startUpload())
        return
    }

    const { folderId, file, broadcast, importAdvertisement, withConversion, onUploaded } = upload
    const query = getURLSearchParamsByLocation(history.location)
    const formData = createFileFormData(query.selectedFolder ? query.selectedFolder : folderId, file, withConversion)
    const isMultipleUpload = Array.isArray(file)
    const uploadMethod = importAdvertisement
        ? 'uploadAdvertisement'
        : broadcast
        ? 'uploadBroadcast'
        : isMultipleUpload
        ? 'uploadMultiplyFiles'
        : 'uploadFile'

    const request = api.post(uploadMethod, formData, createRequestHeaders(mainUser, user, tokens))

    request
        .on('progress', (e) => {
            const progress = e.percent ? Math.round(e.percent) : null

            if (progress) {
                const cancel = () => {
                    isCanceled = true
                    request.abort()
                    dispatch(downloadsActions.removeLoading(id))
                    deleteUploadFile(id)
                }

                updateLoading({
                    type: 'file',
                    id,
                    name: Array.isArray(file) ? file.map(({ name }) => name).join(', ') : file.name,
                    progress,
                })

                updateUploadFileCancel(id, cancel)
            }
        })
        .end((err, res) => {
            dispatch(downloadsActions.removeUpload(id))
            deleteUploadFile(id)

            const responseError = res && res.body && res.body.error

            if (responseError) {
                dispatch(emitFileUploadError(responseError))
            } else if (err && !isCanceled) {
                dispatch(emitFileUploadError(err))
            }

            if (res && res.body && !res.body.error && onUploaded) {
                onUploaded(res.body.data)
            }

            dispatch(startUpload())
        })
})

export const startUpload = createAsyncThunk(
    'downloads/startUpload',
    async (data, { getState, rejectWithValue, dispatch }) => {
        const state = getState() as { downloads: IDownloadsState }
        const { queue } = state.downloads

        if (queue.length >= MAX_QUEUE_LENGTH) return rejectWithValue(null)

        const unstartedUploads = cachedUploads.filter((upload) => {
            const isAllreadyUploading = !!queue.find((id) => id === upload.id)
            return !isAllreadyUploading
        })

        const uploadsToStart = unstartedUploads.slice(0, MAX_QUEUE_LENGTH - queue.length)

        if (!isNotEmptyArray(uploadsToStart)) return rejectWithValue(null)

        uploadsToStart.forEach(({ id }) => dispatch(uploadFile(id)))
        dispatch(downloadsActions.updateQueue(uploadsToStart.map((upload) => upload.id)))
    }
)

export const uploadFiles = createAsyncThunk(
    'downloads/uploadFiles',
    async (payload: UploadFilesPayload, { getState, rejectWithValue, dispatch }) => {
        const { folderId, filesList, broadcast, importAdvertisement, withConversion, onUploaded } = payload

        if (!folderId && !importAdvertisement) {
            emitError('chooseFolder')
            return rejectWithValue(null)
        }

        const newUploads: IDownloadData[] = []
        const isFolderExist = filesList.some((file) => file.path.includes('/'))

        if (isFolderExist) {
            const id = generateIdByUUID()
            const [folderName] = filesList[0].path.split('/', 1)

            setUploadFile({
                id,
                file: filesList,
                folderId: folderId || '',
                broadcast,
                importAdvertisement,
                onUploaded,
            })

            newUploads.push({
                type: 'file',
                name: folderName,
                id,
                progress: 0,
                cancel: true,
            })
        } else {
            filesList.forEach((file) => {
                const id = generateIdByUUID()
                file.type.includes('video')
                    ? setUploadFile({
                          id,
                          file,
                          folderId: folderId || '',
                          broadcast,
                          importAdvertisement,
                          withConversion,
                          onUploaded,
                      })
                    : setUploadFile({
                          id,
                          file,
                          folderId: folderId || '',
                          broadcast,
                          importAdvertisement,
                          onUploaded,
                      })

                newUploads.push({
                    type: 'file',
                    name: file.name,
                    id,
                    progress: 0,
                    cancel: true,
                })
            })
        }

        dispatch(downloadsActions.setUploads(newUploads))
        dispatch(startUpload())
    }
)

const downloadsSlice = createSlice({
    name: 'downloads',
    initialState,
    reducers: {
        setDelayDownloadsProgressList(state, action: PayloadAction<DelayDownloadsProgress[]>) {
            state.delayDownloadsProgressList = action.payload
        },
        addDelayDownloadTask(state, action: PayloadAction<DelayDownloadTask>) {
            state.delayDownloadTasks = [...state.delayDownloadTasks, action.payload]
        },
        deleteDelayDownloadTask(state, action: PayloadAction<string>) {
            state.delayDownloadTasks = state.delayDownloadTasks.filter((task) => task.exportId !== action.payload)
        },
        openModal(state) {
            state.isOpenModal = true
        },
        closeModal(state) {
            state.isOpenModal = false
        },
        toggleModal(state) {
            state.isOpenModal = !state.isOpenModal
        },
        closeModalAndClearDownloads(state) {
            cachedDownloads = []
            state.isShowNotification = true
            state.isOpenModal = false
            state.downloads = []
        },
        clearAllDownloads(state) {
            cachedDownloads = []
            state.isShowNotification = true
            state.downloads = []
        },
        refreshLoadings(state) {
            state.downloads = [...cachedDownloads]
            state.uploads = [...cachedUploads]

            if (!isNotEmptyArray(cachedDownloads) && !state.isShowNotification) {
                state.isShowNotification = true
            }
        },
        clearAllUploads(state) {
            cachedUploads = []
            state.uploads = []
            state.isUploading = false
        },
        addDownload(state, action: PayloadAction<IDownloadRaw>) {
            const downloads = cachedDownloads
            const addedDownload = action.payload

            if (state.isShowNotification && addedDownload.type !== 'sourceFromUrl') {
                state.isShowNotification = false
                showNotification(addedDownload)
            }

            cachedDownloads = addDownload(downloads, addedDownload)
        },
        addDownloads(state, action: PayloadAction<IDownloadRaw[]>) {
            const newDownloads = action.payload
                .map((download) => createDownload(download))
                .filter((download) => isValidDownload(download))

            cachedDownloads = [...newDownloads, ...cachedDownloads]
        },
        updateDownload(state, action: PayloadAction<IDownloadRaw>) {
            const updatedDownload = action.payload
            const downloads = cachedDownloads

            if (updatedDownload && updatedDownload.type === 'sourceFromUrl' && updatedDownload.downloaded) {
                if (
                    updatedDownload.progress === 100 &&
                    cachedUploads.some((upload) => upload.id === updatedDownload.id)
                ) {
                    cachedUploads = cachedUploads.filter((upload) => upload.id !== updatedDownload.id)

                    return
                }

                const data = {
                    name: updatedDownload.name,
                    progress: updatedDownload.progress || 0,
                    downloaded: updatedDownload.downloaded,
                    id: updatedDownload.id,
                    type: updatedDownload.type,
                }

                cachedUploads = [data]

                return
            }

            cachedDownloads = updateDownload(downloads, updatedDownload)
        },
        setUploads(state, action: PayloadAction<IDownloadData[]>) {
            state.isUploading = true
            cachedUploads = [...cachedUploads, ...action.payload]
            cachedDownloads = [...cachedDownloads, ...action.payload]
        },
        removeUpload(state, action: PayloadAction<string>) {
            cachedUploads = cachedUploads.filter((file) => file.id !== action.payload)

            state.uploads = cachedUploads
            state.queue = state.queue.filter((id) => id !== action.payload)

            if (!isNotEmptyArray(cachedUploads)) {
                state.isUploading = false
            }
        },
        removeLoading(state, action: PayloadAction<string>) {
            cachedUploads = cachedUploads.filter((file) => file.id !== action.payload)
            cachedDownloads = cachedDownloads.filter((file) => file.id !== action.payload)

            state.uploads = cachedUploads
            state.downloads = cachedDownloads
            state.queue = state.queue.filter((id) => id !== action.payload)

            if (!isNotEmptyArray(cachedUploads)) {
                state.isUploading = false
            }
        },
        removeDownload(state, action: PayloadAction<Partial<IDownloadBroadcast> | Partial<IDownloadFile>>) {
            cachedDownloads = cachedDownloads.filter((download) => !isEqual(download, action.payload))
            state.downloads = cachedDownloads

            if (!isNotEmptyArray(cachedDownloads) && !state.isShowNotification) {
                state.isShowNotification = true
            }
        },
        removeDigitalSignage(state, action: PayloadAction<IDownloadDevice>) {
            cachedDownloads = removeDigitalSignage(cachedDownloads, action.payload)
            state.downloads = cachedDownloads

            if (!isNotEmptyArray(cachedDownloads) && !state.isShowNotification) {
                state.isShowNotification = true
            }
        },
        updateQueue(state, action: PayloadAction<string[]>) {
            state.queue = [...state.queue, ...action.payload]
        },
    },
})

const { reducer: downloadsReducer, actions: downloadsActions } = downloadsSlice
export { downloadsReducer, downloadsActions }
